import {
  calculateCartItemTotal,
  calculateCartSubtotal,
  calculateOrderItemTotal,
  calculateOrderTotal,
} from "@lemmonhq/orders";
import {
  Cart,
  CheckoutData,
  Promotion,
  PromotionRequirement,
  PromotionReward,
  AppliedPromotion,
  AppliedPromotionEligibleItem,
  TableCheckout,
  Service,
  ProductRequirement,
  ProductCategoryRequirement,
  ProductDiscountReward,
  SpecialPriceReward,
  Order,
  OrderItem,
  LoyaltyRewardSelection,
  ProductCategoryReward,
  ProductReward,
} from "@lemmonhq/types";
import {
  CheckoutItem,
  getItemProductId,
  getProductIdsFromCategories,
} from "./item-utils";
import { isTimestampEligibleForPromotion } from "./time-utils";

export function calculatePromotionApplications(
  services: Service[],
  promotion: Promotion,
  checkout: CheckoutData,
  context?: {
    selection?: LoyaltyRewardSelection;
    redemptionUnit?: number;
  },
): AppliedPromotion[] {
  console.log("[PROMO-CALC] Calculating applications for:", promotion.name);
  console.log("[PROMO-CALC] Reward type:", promotion.reward.type);
  console.log("[PROMO-CALC] Checkout type:", checkout.payload?.type);

  if (checkout.payload?.type === "cart") {
    const result = calculateCartPromotionApplications(
      services,
      promotion,
      checkout.payload.object,
      context,
    );
    console.log("[PROMO-CALC] Cart applications:", result);
    return result;
  } else if (checkout.payload?.type === "table") {
    const result = calculateTablePromotionApplications(
      services,
      promotion,
      checkout.payload.object,
      context,
    );
    console.log("[PROMO-CALC] Table applications:", result);
    return result;
  }

  console.log("[PROMO-CALC] ❌ Unknown checkout type, returning empty");
  return [];
}

function calculateCartPromotionApplications(
  services: Service[],
  promotion: Promotion,
  cart: Cart,
  context?: {
    selection?: LoyaltyRewardSelection;
    redemptionUnit?: number;
  },
): AppliedPromotion[] {
  const reward = promotion.reward;
  console.log("[PROMO-CALC] Cart has", cart.items.length, "items");
  console.log("[PROMO-CALC] Processing reward type:", reward.type);

  switch (reward.type) {
    case "order_discount":
      return calculateOrderDiscountForCart(services, promotion, cart, reward);

    case "product_discount":
      return calculateProductDiscountForCart(services, promotion, cart, reward);

    case "special_price":
      return calculateSpecialPriceForCart(services, promotion, cart, reward);

    case "product":
    case "product_category":
      return calculateFreeProductForCart(
        services,
        promotion,
        cart,
        reward,
        context,
      );

    default:
      console.warn(
        `[PROMO-CALC] ❌ Unknown reward type: ${(reward as any).type}`,
      );
      return [];
  }
}

/**
 * Filter orders to only include those created during the promotion's active time
 */
function filterOrdersByPromotionTime(
  orders: Order[],
  promotion: Promotion,
): Order[] {
  return orders.filter((order) => {
    const isEligible = isTimestampEligibleForPromotion(
      order.created_at,
      promotion,
    );
    if (!isEligible) {
      console.log(
        `[PROMO-CALC] Order ${order.id} created at ${order.created_at} - outside promotion time, skipping`,
      );
    }
    return isEligible;
  });
}

/**
 * Filter items within an order to only include those created during the promotion's active time
 */
function filterOrderItemsByPromotionTime(
  order: Order,
  promotion: Promotion,
): OrderItem[] {
  if (!order.items) return [];

  return order.items.filter((item) => {
    const isEligible = isTimestampEligibleForPromotion(
      item.created_at,
      promotion,
    );
    if (!isEligible) {
      console.log(
        `[PROMO-CALC] Item ${item.id} (product ${item.product_id}) created at ${item.created_at} - outside promotion time, skipping`,
      );
    }
    return isEligible;
  });
}

function calculateTablePromotionApplications(
  services: Service[],
  promotion: Promotion,
  tableCheckout: TableCheckout,
  context?: {
    selection?: LoyaltyRewardSelection;
    redemptionUnit?: number;
  },
): AppliedPromotion[] {
  const reward = promotion.reward;
  const applications: AppliedPromotion[] = [];

  // Filter orders to only include those placed during promotion time
  const eligibleOrders = filterOrdersByPromotionTime(
    tableCheckout.orders,
    promotion,
  );
  console.log(
    `[PROMO-CALC] Filtered ${tableCheckout.orders.length} orders to ${eligibleOrders.length} eligible orders`,
  );

  if (eligibleOrders.length === 0) {
    console.log(
      "[PROMO-CALC] No orders placed during promotion time, returning empty",
    );
    return [];
  }

  // For split checkouts, calculate the split ratio based on eligible orders only
  let splitRatio = 1;
  if (tableCheckout.type === "split") {
    const totalAmount = tableCheckout.orders.reduce(
      (sum, order) => sum + calculateOrderTotal(order),
      0,
    );
    splitRatio = totalAmount > 0 ? tableCheckout.splitAmount / totalAmount : 0;
    console.log(
      `[PROMO-CALC] Split checkout - splitAmount: ${tableCheckout.splitAmount}, totalAmount (eligible): ${totalAmount}, ratio: ${splitRatio}`,
    );
  }

  const selectedPartialItemKeys =
    tableCheckout.type === "partial"
      ? new Set(tableCheckout.items.map((item) => `${item.orderId}:${item.id}`))
      : null;

  switch (reward.type) {
    case "order_discount":
      if (tableCheckout.type === "full") {
        // For full checkout, apply discount to each eligible order separately
        for (const order of eligibleOrders) {
          if (order.items && order.items.length > 0) {
            const orderSubtotal = calculateOrderTotal(order);
            const discountValue = calculateDiscountValue(reward, orderSubtotal);
            if (discountValue > 0) {
              applications.push(
                createAppliedPromotion(
                  promotion,
                  discountValue,
                  (order.items ?? []).map((item) =>
                    createOrderEligibleItem(item),
                  ),
                ),
              );
            }
          }
        }
      } else if (tableCheckout.type === "split") {
        // For split checkout, calculate discount on eligible order total, then apply split ratio
        const totalAmount = eligibleOrders.reduce(
          (sum, order) => sum + calculateOrderTotal(order),
          0,
        );
        const fullDiscountValue = calculateDiscountValue(reward, totalAmount);
        const proportionalDiscount = fullDiscountValue * splitRatio;
        if (proportionalDiscount > 0) {
          applications.push(
            createAppliedPromotion(
              promotion,
              proportionalDiscount,
              eligibleOrders.flatMap((order) =>
                (order.items ?? []).map((item) =>
                  createOrderEligibleItem(item),
                ),
              ),
            ),
          );
        }
      } else if (tableCheckout.type === "partial") {
        // For partial checkout, calculate only selected items from orders that
        // are eligible for this promotion's time window.
        const eligibleOrderIds = new Set(
          eligibleOrders.map((order) => order.id),
        );
        const eligiblePartialItems = tableCheckout.items.filter((item) =>
          eligibleOrderIds.has(item.orderId),
        );
        const partialTotal = eligiblePartialItems.reduce(
          (sum, item) => sum + item.total,
          0,
        );
        const discountValue = calculateDiscountValue(reward, partialTotal);
        if (discountValue > 0) {
          applications.push(
            createAppliedPromotion(
              promotion,
              discountValue,
              eligiblePartialItems.map((item) => ({
                order_item_id: item.id,
                quantity: item.quantity,
              })),
            ),
          );
        }
      }
      break;

    case "product_discount":
      // Apply to each qualifying product across all eligible orders
      for (const order of eligibleOrders) {
        // Filter items by promotion time
        const eligibleItems = filterOrderItemsByPromotionTime(order, promotion);

        for (const item of eligibleItems) {
          const partialItem =
            tableCheckout.type === "partial"
              ? tableCheckout.items.find(
                  (selected) =>
                    selected.orderId === item.order_id &&
                    selected.id === item.id,
                )
              : undefined;
          if (tableCheckout.type === "partial" && !partialItem) {
            continue;
          }

          if (
            isItemQualifyingForProductDiscount(
              services,
              item,
              reward,
              promotion.requirements,
            )
          ) {
            const eligibleQuantity = partialItem?.quantity ?? item.quantity;
            const totalItemPrice = item.price * eligibleQuantity;
            const discountValue = calculateDiscountValueForItem(
              reward,
              totalItemPrice,
              eligibleQuantity,
            );
            if (discountValue > 0) {
              // Apply split ratio for split checkouts
              const adjustedDiscount =
                tableCheckout.type === "split"
                  ? discountValue * splitRatio
                  : discountValue;
              applications.push(
                createAppliedPromotion(promotion, adjustedDiscount, [
                  createOrderEligibleItem(item, eligibleQuantity),
                ]),
              );
            }
          }
        }
      }
      break;

    case "special_price":
      console.log(
        "[PROMO-CALC] Table special_price - processing",
        eligibleOrders.length,
        "eligible orders",
      );
      console.log("[PROMO-CALC] Special prices defined:", reward.prices);
      // Apply special price to qualifying products across all eligible orders
      for (const order of eligibleOrders) {
        console.log("[PROMO-CALC] Checking order", order.id);

        // Filter items by promotion time
        const eligibleItems = filterOrderItemsByPromotionTime(order, promotion);
        console.log(
          "[PROMO-CALC]   Filtered to",
          eligibleItems.length,
          "eligible items",
        );

        for (const item of eligibleItems) {
          const partialItem =
            tableCheckout.type === "partial"
              ? tableCheckout.items.find(
                  (selected) =>
                    selected.orderId === item.order_id &&
                    selected.id === item.id,
                )
              : undefined;
          if (
            selectedPartialItemKeys &&
            (!partialItem ||
              !selectedPartialItemKeys.has(`${item.order_id}:${item.id}`))
          ) {
            continue;
          }

          const productId = item.product_id;
          console.log(
            `[PROMO-CALC] Checking item - productId: ${productId}, price: ${item.price}, quantity: ${item.quantity}`,
          );

          if (reward.prices[productId] !== undefined) {
            const specialPrice = reward.prices[productId];
            const itemPrice =
              typeof item.price === "string"
                ? parseFloat(item.price)
                : item.price;
            const eligibleQuantity = partialItem?.quantity ?? item.quantity;
            const originalTotal = itemPrice * eligibleQuantity;
            const specialPriceTotal = specialPrice * eligibleQuantity;
            const discountValue = Math.max(
              0,
              originalTotal - specialPriceTotal,
            );

            console.log(
              `[PROMO-CALC] Product ${productId} HAS special price: ${specialPrice}`,
            );
            console.log(`[PROMO-CALC]   Item price: ${itemPrice}`);
            console.log(
              `[PROMO-CALC]   Original total: ${originalTotal}, Special total: ${specialPriceTotal}, Discount: ${discountValue}`,
            );

            if (discountValue > 0) {
              // Apply split ratio for split checkouts
              const adjustedDiscount =
                tableCheckout.type === "split"
                  ? discountValue * splitRatio
                  : discountValue;
              applications.push(
                createAppliedPromotion(promotion, adjustedDiscount, [
                  createOrderEligibleItem(item, eligibleQuantity),
                ]),
              );
              console.log("[PROMO-CALC]   ✅ Added application");
            } else {
              console.log(
                "[PROMO-CALC]   ⚠️ Discount is 0 or negative, skipping",
              );
            }
          } else {
            console.log(
              `[PROMO-CALC] Product ${productId} NOT in special prices`,
            );
          }
        }
      }
      console.log(
        "[PROMO-CALC] Total special price applications:",
        applications.length,
      );
      break;

    case "product":
    case "product_category":
      applications.push(
        ...calculateFreeProductForTable(
          services,
          promotion,
          tableCheckout,
          eligibleOrders,
          reward,
          context,
        ),
      );
      break;
  }

  return applications;
}

function calculateOrderDiscountForCart(
  services: Service[],
  promotion: Promotion,
  cart: Cart,
  reward: { mode: "percentage" | "absolute"; value: number },
): AppliedPromotion[] {
  const subtotal = calculateCartSubtotal(cart);
  const discountValue = calculateDiscountValue(reward, subtotal);

  if (discountValue > 0) {
    return [
      createAppliedPromotion(
        promotion,
        discountValue,
        cart.items.map((item) => createCartEligibleItem(item)),
      ),
    ];
  }
  return [];
}

function calculateProductDiscountForCart(
  services: Service[],
  promotion: Promotion,
  cart: Cart,
  reward: ProductDiscountReward,
): AppliedPromotion[] {
  const applications: AppliedPromotion[] = [];

  for (const item of cart.items) {
    if (
      isItemQualifyingForProductDiscount(
        services,
        item,
        reward,
        promotion.requirements,
      )
    ) {
      const totalItemPrice = calculateCartItemTotal(item);
      const discountValue = calculateDiscountValueForItem(
        reward,
        totalItemPrice,
        item.quantity,
      );

      if (discountValue > 0) {
        applications.push(
          createAppliedPromotion(promotion, discountValue, [
            createCartEligibleItem(item),
          ]),
        );
      }
    }
  }

  return applications;
}

function calculateSpecialPriceForCart(
  services: Service[],
  promotion: Promotion,
  cart: Cart,
  reward: SpecialPriceReward,
): AppliedPromotion[] {
  console.log("[PROMO-CALC] Calculating special price for cart");
  console.log("[PROMO-CALC] Special prices defined:", reward.prices);
  const applications: AppliedPromotion[] = [];

  for (const item of cart.items) {
    const productId = getItemProductId(item);
    console.log("[PROMO-CALC] Checking item - productId:", productId);

    // Check if this product has a special price defined
    if (reward.prices[productId] !== undefined) {
      const specialPrice = reward.prices[productId];
      const originalTotal = calculateCartItemTotal(item);
      const specialPriceTotal = specialPrice * item.quantity;
      const discountValue = Math.max(0, originalTotal - specialPriceTotal);

      console.log(
        `[PROMO-CALC] Product ${productId}: original=${originalTotal}, special=${specialPriceTotal}, discount=${discountValue}`,
      );

      if (discountValue > 0) {
        applications.push(
          createAppliedPromotion(promotion, discountValue, [
            createCartEligibleItem(item),
          ]),
        );
        console.log("[PROMO-CALC] ✅ Added application for product", productId);
      } else {
        console.log("[PROMO-CALC] ⚠️ Discount is 0 or negative, skipping");
      }
    } else {
      console.log(
        "[PROMO-CALC] ⚠️ Product",
        productId,
        "not in special prices",
      );
    }
  }

  console.log("[PROMO-CALC] Special price applications:", applications.length);
  return applications;
}

function calculateFreeProductForCart(
  services: Service[],
  promotion: Promotion,
  cart: Cart,
  reward: ProductReward | ProductCategoryReward,
  context?: {
    selection?: LoyaltyRewardSelection;
    redemptionUnit?: number;
  },
): AppliedPromotion[] {
  const allowedProductIds = getRewardProductIds(
    services,
    reward,
    promotion.requirements,
  );
  const candidates = cart.items
    .filter((item) => allowedProductIds.has(item.productId))
    .map((item) => ({
      productId: item.productId,
      name: item.name,
      quantity: item.quantity,
      itemId: item.cartId,
      unitValue: calculateCartItemTotal(item) / item.quantity,
      eligibleItem: (quantity: number): AppliedPromotionEligibleItem => ({
        cart_item_id: item.cartId,
        quantity,
      }),
    }));
  return createFreeProductApplication(promotion, reward, candidates, context);
}

function calculateFreeProductForTable(
  services: Service[],
  promotion: Promotion,
  tableCheckout: TableCheckout,
  eligibleOrders: Order[],
  reward: ProductReward | ProductCategoryReward,
  context?: {
    selection?: LoyaltyRewardSelection;
    redemptionUnit?: number;
  },
): AppliedPromotion[] {
  // A monetary split is not item-scoped, so there is no honest way to decide
  // which product this leg is buying. Full and partial checkouts are explicit.
  if (tableCheckout.type === "split") return [];
  const allowedProductIds = getRewardProductIds(
    services,
    reward,
    promotion.requirements,
  );
  const partialQuantities =
    tableCheckout.type === "partial"
      ? new Map(tableCheckout.items.map((item) => [item.id, item.quantity]))
      : null;
  const candidates = eligibleOrders.flatMap((order) =>
    filterOrderItemsByPromotionTime(order, promotion)
      .filter((item) => allowedProductIds.has(item.product_id))
      .map((item) => ({
        productId: item.product_id,
        name: item.product?.name ?? rewardProductName(reward, item.product_id),
        quantity: partialQuantities
          ? (partialQuantities.get(item.id) ?? 0)
          : Math.max(0, item.quantity - (item.paid_quantity ?? 0)),
        itemId: item.id,
        unitValue: calculateOrderItemTotal(item, 1),
        eligibleItem: (quantity: number): AppliedPromotionEligibleItem => ({
          order_item_id: item.id,
          quantity,
        }),
      }))
      .filter((item) => item.quantity > 0),
  );
  return createFreeProductApplication(promotion, reward, candidates, context);
}

type FreeProductCandidate = {
  productId: number;
  name: string;
  quantity: number;
  itemId: string | number;
  unitValue: number;
  eligibleItem: (quantity: number) => AppliedPromotionEligibleItem;
};

function getRewardProductIds(
  services: Service[],
  reward: ProductReward | ProductCategoryReward,
  requirements: PromotionRequirement[],
) {
  if (reward.type === "product_category") {
    return new Set(
      getProductIdsFromCategories(
        services,
        reward.categories.map((category) => category.id),
      ),
    );
  }
  if (reward.product_id != null) return new Set([reward.product_id]);
  const direct = requirements.flatMap((requirement) =>
    requirement.type === "product" ? requirement.product_ids : [],
  );
  const categoryIds = requirements.flatMap((requirement) =>
    requirement.type === "product_category" ? requirement.category_ids : [],
  );
  return new Set([
    ...direct,
    ...getProductIdsFromCategories(services, categoryIds),
  ]);
}

function rewardProductName(
  reward: ProductReward | ProductCategoryReward,
  productId: number,
) {
  return reward.type === "product" && reward.product_id === productId
    ? (reward.product_name ?? `#${productId}`)
    : `#${productId}`;
}

function createFreeProductApplication(
  promotion: Promotion,
  reward: ProductReward | ProductCategoryReward,
  candidates: FreeProductCandidate[],
  context?: {
    selection?: LoyaltyRewardSelection;
    redemptionUnit?: number;
  },
): AppliedPromotion[] {
  const unit = Math.max(1, context?.redemptionUnit ?? 1);
  const choices = context?.selection?.product_choices;
  const permitted = candidates.map((candidate) => {
    const choice = choices?.find(
      (item) =>
        item.product_id === candidate.productId &&
        (item.cart_item_id === candidate.itemId ||
          item.order_item_id === candidate.itemId),
    );
    return {
      ...candidate,
      quantity: choices
        ? Math.min(candidate.quantity, choice?.quantity ?? 0)
        : candidate.quantity,
    };
  });
  // Category rewards always require an explicit cashier/customer choice.
  if (reward.type === "product_category" && !choices?.length) return [];

  let skip = (unit - 1) * reward.quantity;
  let remaining = reward.quantity;
  const selected: { candidate: FreeProductCandidate; quantity: number }[] = [];
  for (const candidate of permitted) {
    if (skip >= candidate.quantity) {
      skip -= candidate.quantity;
      continue;
    }
    const available = candidate.quantity - skip;
    skip = 0;
    const quantity = Math.min(remaining, available);
    if (quantity > 0) selected.push({ candidate, quantity });
    remaining -= quantity;
    if (remaining <= 0) break;
  }
  if (remaining > 0) return [];
  const value = selected.reduce(
    (sum, item) => sum + item.candidate.unitValue * item.quantity,
    0,
  );
  if (value <= 0) return [];
  return [
    createAppliedPromotion(
      promotion,
      value,
      selected.map(({ candidate, quantity }) =>
        candidate.eligibleItem(quantity),
      ),
      {
        reward,
        redeemed_products: selected.map(({ candidate, quantity }) => ({
          id: candidate.productId,
          name: candidate.name,
          quantity,
        })),
      },
    ),
  ];
}

export function calculateDiscountValue(
  reward: { mode: "percentage" | "absolute"; value: number },
  baseAmount: number,
): number {
  if (reward.mode === "percentage") {
    const discountAmount = (baseAmount * reward.value) / 100;
    return Math.round(discountAmount * 100) / 100;
  } else {
    return Math.min(reward.value, baseAmount);
  }
}

function isItemQualifyingForProductDiscount(
  services: Service[],
  item: CheckoutItem,
  reward: PromotionReward,
  requirements: PromotionRequirement[],
): boolean {
  const productRequirements = requirements.filter(
    (req): req is ProductRequirement => req.type === "product",
  );
  const categoryRequirements = requirements.filter(
    (req): req is ProductCategoryRequirement => req.type === "product_category",
  );

  // If there are no product or category-specific requirements, the discount applies to any item.
  if (productRequirements.length === 0 && categoryRequirements.length === 0) {
    return true;
  }

  const productId = getItemProductId(item);

  // Check if the item's product ID is in any of the explicit product requirements
  if (productRequirements.some((req) => req.product_ids.includes(productId))) {
    return true;
  }

  // Check if the item's product ID belongs to any of the required categories
  if (categoryRequirements.length > 0) {
    const qualifyingCategoryIds = categoryRequirements.flatMap(
      (req) => req.category_ids,
    );
    const productIdsFromCategories = getProductIdsFromCategories(
      services,
      qualifyingCategoryIds,
    );
    if (productIdsFromCategories.includes(productId)) {
      return true;
    }
  }

  return false;
}

function createAppliedPromotion(
  promotion: Promotion,
  value: number,
  eligibleItems: AppliedPromotionEligibleItem[],
  display?: AppliedPromotion["display"],
): AppliedPromotion {
  return {
    application_id: crypto.randomUUID(),
    promotion_id: promotion.id,
    promotion_name: promotion.name,
    reward_type: promotion.reward.type,
    value,
    eligible_items: eligibleItems,
    calculation_version: 1,
    tax_treatment: "reduces_taxable_base",
    display,
  };
}

function createCartEligibleItem(
  item: Cart["items"][number],
): AppliedPromotionEligibleItem {
  return {
    cart_item_id: item.cartId,
    quantity: item.quantity,
  };
}

function createOrderEligibleItem(
  item: OrderItem,
  quantity = item.quantity,
): AppliedPromotionEligibleItem {
  return {
    order_item_id: item.id,
    quantity,
  };
}

export function calculateDiscountValueForItem(
  reward: { mode: "percentage" | "absolute"; value: number },
  baseAmount: number,
  quantity: number,
): number {
  if (reward.mode === "percentage") {
    const discountAmount = (baseAmount * reward.value) / 100;
    return Math.round(discountAmount * 100) / 100;
  } else {
    return Math.min(reward.value * quantity, baseAmount);
  }
}
